import React from "react";
import { Link, Head } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { CALENDLY_COST_REVIEW_URL } from "@/lib/urls";
import {
  BarChart2,
  Search,
  GitPullRequest,
  ShieldCheck,
  ChevronRight,
  Layers,
  Activity,
  Calendar,
  RefreshCw,
  CheckCircle,
} from "lucide-react";
import Layout from "../Layout";
import { CallToAction } from "@/components/marketing/CallToAction";
import {
  LatestArticles,
  type Article,
} from "@/components/marketing/LatestArticles";
import { FAQ, type FAQItem } from "@/components/marketing/FAQ";
import {
  PullRequestPreview,
  InlineCode,
} from "@/components/marketing/PullRequestPreview";
import { DiffPreview, DiffLine } from "@/components/marketing/DiffPreview";
import { SlackMessagePreview } from "@/components/marketing/SlackMessagePreview";
import { ProductRecommendationsMockup } from "@/components/marketing/ProductRecommendationsMockup";
import { ProductHowItWorks } from "@/components/marketing/ProductHowItWorks";
import { ProductThresholds } from "@/components/marketing/ProductThresholds";
import { ProductGuardrails } from "@/components/marketing/ProductGuardrails";

// ── Hero ─────────────────────────────────────────────────────────────────────

const BREADCRUMB_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://infralyst.io",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "ASG Rightsizing",
      item: "https://infralyst.io/products/auto-scaling-groups",
    },
  ],
});

const SOFTWARE_APP_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Infralyst ASG Rightsizing",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Infralyst finds over-provisioned ASGs and generates Terraform PRs to reduce your AWS costs, updating the launch template with zero downtime instance refresh.",
  url: "https://infralyst.io/products/auto-scaling-groups",
  offers: {
    "@type": "AggregateOffer",
    lowPrice: "0",
    highPrice: "99",
    priceCurrency: "USD",
    offerCount: 2,
    offers: [
      {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free plan with 3 PRs per workspace",
      },
      {
        "@type": "Offer",
        price: "99",
        priceCurrency: "USD",
        description: "Pro plan with unlimited PRs per workspace, billed monthly",
      },
    ],
  },
  publisher: {
    "@type": "Organization",
    name: "Infralyst",
    url: "https://infralyst.io",
  },
});

const RECOMMENDATIONS = [
  {
    name: "web-api-prod",
    subtext: "us-east-1 · 84 days",
    current: "c5.2xlarge",
    recommended: "c5.xlarge",
    cpuAvg: "5.1%",
    cpuPct: "21%",
    memAvg: "9%",
    memPct: "18%",
    scale: "3/6",
    savings: "$94/mo",
    days: 84,
    region: "us-east-1",
  },
  {
    name: "worker-queue",
    subtext: "us-east-1 · 60 days",
    current: "m5.xlarge",
    recommended: "m5.large",
    cpuAvg: "7.3%",
    cpuPct: "19%",
    memAvg: "11%",
    memPct: "23%",
    scale: "2/4",
    savings: "$47/mo",
    days: 60,
    region: "us-east-1",
  },
  {
    name: "cache-cluster",
    subtext: "us-east-1 · 45 days",
    current: "r5.large",
    recommended: "r5.medium",
    cpuAvg: "6.2%",
    cpuPct: "17%",
    memAvg: "14%",
    memPct: "28%",
    scale: "5/5",
    savings: "$36/mo",
    days: 45,
    region: "us-east-1",
  },
];

function Hero(): React.ReactElement {
  return (
    <>
      <Head>
        <script type="application/ld+json">{BREADCRUMB_JSON_LD}</script>
        <script type="application/ld+json">{SOFTWARE_APP_JSON_LD}</script>
      </Head>
      <section className="px-4 pt-28 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-12 items-center">
            {/* Left: text */}
            <div>
              {/* Breadcrumb */}
              <nav
                aria-label="Breadcrumb"
                className="flex items-center gap-1 text-sm text-surface-foreground/50 mb-6"
              >
                <span>Products</span>
                <ChevronRight className="size-3.5" />
                <span className="flex items-center gap-1 text-foreground font-medium">
                  <Layers className="size-3.5" />
                  Auto Scaling Groups
                </span>
              </nav>

              <h1 className="mb-5 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
                ASG <span className="text-primary">Rightsizing</span>
              </h1>
              <p className="mb-8 text-xl leading-relaxed text-surface-foreground/80">
                Rightsizing ASG fleets means analyzing every instance, picking
                the right type, updating the launch template, and rolling it out
                without downtime. Infralyst handles all of it in a single
                Terraform PR.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Button asChild size="lg">
                  <Link href="/signup">Start free with 3 PRs</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <a href={CALENDLY_COST_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                    Book a demo
                  </a>
                </Button>
              </div>
              <ul className="flex flex-col gap-2">
                {["Unlimited free recommendations", "Read-only IAM role", "Your team reviews and merges every change"].map((point) => (
                  <li key={point} className="flex items-center gap-2 text-sm text-surface-foreground/60">
                    <CheckCircle className="size-4 text-green-500 flex-none" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: UI mockup */}
            <div className="lg:pl-4">
              <ProductRecommendationsMockup
                title="ASG Recommendations"
                icon={Layers}
                recommendations={RECOMMENDATIONS}
                footerText="Analyzed 19 ASGs across 3 regions"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ── The Problem ───────────────────────────────────────────────────────────────

function TheProblem(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-4xl font-bold mb-6">
          Your launch templates are set
          <br />
          and <span className="text-primary">never revisited</span>
        </h2>
        <p className="text-lg leading-relaxed text-surface-foreground/80 mb-4">
          Teams pick an instance type when they first create an ASG, usually
          sized for anticipated peak load. Workloads change, utilization drops,
          but the launch template doesn't. Nobody wants to touch it because
          changing the instance type means coordinating a fleet-wide instance
          refresh without dropping traffic. So the waste compounds quietly.
        </p>
        <p className="text-lg leading-relaxed text-surface-foreground/80">
          AWS Cost Explorer will flag the opportunity. But it stops at "you
          could save money here." Infralyst does the rest. It analyzes real
          utilization across every instance in the ASG, selects a conservative
          target, and generates a Terraform PR that updates your launch template
          and adds an instance refresh block for zero downtime rollout.
        </p>
      </div>
    </section>
  );
}

// ── How the Analysis Works ────────────────────────────────────────────────────

const ANALYSIS_STEPS = [
  {
    icon: BarChart2,
    title: "1. Collect utilization metrics",
    description:
      "Infralyst pulls CPU and memory utilization from CloudWatch for every instance in the ASG, using up to 365 days of historical data.",
  },
  {
    icon: Search,
    title: "2. Run the analysis gates",
    description:
      "Each ASG passes through data quality validation, utilization thresholds, and seasonality checks. Every instance in the fleet has to clear the bar. An ASG only gets a recommendation if all gates pass.",
  },
  {
    icon: GitPullRequest,
    title: "3. Generate the recommendation",
    description:
      "Infralyst recommends the next size down in the same instance family. When you're ready, an AI agent reads your Terraform, writes the change, and validates it. The analysis is fully deterministic.",
  },
] as const;

// ── Thresholds & Methodology ──────────────────────────────────────────────────

const GATES = [
  {
    gate: "Pre-flight",
    icon: ShieldCheck,
    checks: [
      { metric: "Ephemeral storage", threshold: "No instance store volumes" },
      { metric: "Minimum size", threshold: "Not already smallest in family" },
    ],
  },
  {
    gate: "Data quality",
    icon: BarChart2,
    checks: [
      { metric: "Data window", threshold: "≥ 30 days of metric data" },
      {
        metric: "Data point coverage",
        threshold: "≥ 8,640 total data points",
      },
    ],
  },
  {
    gate: "CPU & Memory",
    icon: Activity,
    checks: [
      { metric: "P99.5 · 1–60 days (5 min)", threshold: "< 40%" },
      { metric: "P95 · 60–365 days (hourly)", threshold: "< 40%" },
    ],
  },
  {
    gate: "Seasonality",
    icon: Calendar,
    description:
      "For ASGs with 60–365 days of data, P95 is checked per weekly bucket. If any week exceeds the 40% threshold, the ASG is excluded.",
    checks: [],
  },
] as const;

// ── Trigger PRs from Slack ────────────────────────────────────────────────────

const SLACK_REC = RECOMMENDATIONS[0];

function TriggerFromSlack(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Trigger PRs{" "}
          <span className="text-primary">directly from Slack</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-10 max-w-2xl mx-auto">
          Get notified when new savings are found. Review the details and
          generate a PR without leaving Slack.
        </p>

        <div className="max-w-xl mx-auto overflow-x-auto">
          <div className="min-w-[500px]">
            <SlackMessagePreview>
              {/* Header */}
              <p className="font-bold text-[15px] leading-snug mb-5">
                New ASG savings recommendation · Save $94.00/mo
              </p>

              {/* All fields in one unified grid */}
              <div className="grid grid-cols-2 gap-x-10 mb-1">
                <div className="mb-4">
                  <p className="font-bold text-sm">Resource</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.name} ({SLACK_REC.region})
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Lookback</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.days}d
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">CPU Avg</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.cpuAvg}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">CPU P99.5</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.cpuPct}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Mem Avg</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.memAvg}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Mem P99.5</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.memPct}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Current</p>
                  <span className="font-mono text-sm text-surface-foreground/60">
                    {SLACK_REC.current}
                  </span>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Recommended</p>
                  <span className="font-mono text-sm text-primary font-medium">
                    {SLACK_REC.recommended}
                  </span>
                </div>
              </div>

              {/* Link */}
              <p className="text-xs text-primary mb-3">View on Infralyst</p>

              {/* Buttons */}
              <div className="flex items-center gap-2">
                <button className="text-xs font-bold px-3 py-1.5 rounded-md bg-primary text-primary-foreground">
                  Generate PR
                </button>
                <button className="text-xs font-bold px-3 py-1.5 rounded-md border border-foreground/25 text-foreground/70">
                  Dismiss
                </button>
              </div>
            </SlackMessagePreview>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── What the PR Looks Like ────────────────────────────────────────────────────

const ASG_PR_BODY = (
  <>
    <p>
      Auto Scaling Group <InlineCode>web-api-prod</InlineCode> in{" "}
      <InlineCode>us-east-1</InlineCode> can be downsized from{" "}
      <InlineCode>c5.2xlarge</InlineCode> to <InlineCode>c5.xlarge</InlineCode>,
      saving an estimated $94/mo.
    </p>

    <table className="w-full text-sm border-collapse mb-4">
      <thead>
        <tr className="border-b border-foreground/20">
          <th className="text-left py-1 pr-4">Metric</th>
          <th className="text-left py-1 pr-4">Avg</th>
          <th className="text-left py-1">P99.5</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-foreground/10">
          <td className="py-1 pr-4">CPU</td>
          <td className="py-1 pr-4">5.1%</td>
          <td className="py-1">21%</td>
        </tr>
        <tr>
          <td className="py-1 pr-4">Memory</td>
          <td className="py-1 pr-4">9%</td>
          <td className="py-1">18%</td>
        </tr>
      </tbody>
    </table>

    <p className="text-sm text-foreground/70">Lookback: 84 days</p>

    <p>
      <a
        href="https://infralyst.io/workspaces/acme-corp/aws_accounts/mock-uuid"
        className="text-primary underline"
      >
        View on Infralyst
      </a>
    </p>
  </>
);

const ASG_DIFF_LINES: DiffLine[] = [
  {
    type: "hunk",
    content: '@@ -12,7 +12,7 @@ resource "aws_launch_template" "web_api" {',
  },
  { type: "context", content: "", oldLineNumber: 12, newLineNumber: 12 },
  {
    type: "context",
    content: 'resource "aws_launch_template" "web_api" {',
    oldLineNumber: 13,
    newLineNumber: 13,
  },
  {
    type: "context",
    content: '  name_prefix = "web-api-"',
    oldLineNumber: 14,
    newLineNumber: 14,
  },
  {
    type: "removed",
    content: '  instance_type = "c5.2xlarge"',
    oldLineNumber: 15,
  },
  {
    type: "added",
    content: '  instance_type = "c5.xlarge"',
    newLineNumber: 15,
  },
  {
    type: "context",
    content: "}",
    oldLineNumber: 16,
    newLineNumber: 16,
  },
  {
    type: "hunk",
    content: '@@ -28,6 +28,13 @@ resource "aws_autoscaling_group" "web_api" {',
  },
  {
    type: "context",
    content: "  launch_template {",
    oldLineNumber: 28,
    newLineNumber: 28,
  },
  {
    type: "context",
    content: "    id      = aws_launch_template.web_api.id",
    oldLineNumber: 29,
    newLineNumber: 29,
  },
  {
    type: "context",
    content: '    version = "$Latest"',
    oldLineNumber: 30,
    newLineNumber: 30,
  },
  {
    type: "context",
    content: "  }",
    oldLineNumber: 31,
    newLineNumber: 31,
  },
  {
    type: "added",
    content: "  instance_refresh {",
    newLineNumber: 32,
  },
  {
    type: "added",
    content: '    strategy = "Rolling"',
    newLineNumber: 33,
  },
  {
    type: "added",
    content: "    preferences {",
    newLineNumber: 34,
  },
  {
    type: "added",
    content: "      min_healthy_percentage = 90",
    newLineNumber: 35,
  },
  {
    type: "added",
    content: "    }",
    newLineNumber: 36,
  },
  {
    type: "added",
    content: "  }",
    newLineNumber: 37,
  },
  {
    type: "context",
    content: "}",
    oldLineNumber: 32,
    newLineNumber: 38,
  },
];

function WhatThePrLooksLike(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          What the <span className="text-primary">PR looks like</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-10 max-w-2xl mx-auto">
          Infralyst opens a Terraform pull request in your repo. Your team
          reviews and merges it like any other change.
        </p>

        <PullRequestPreview
          title="Downsize ASG web-api-prod: c5.2xlarge → c5.xlarge (-$94/mo)"
          author="infralyst"
          status="open"
          body={ASG_PR_BODY}
          filesChangedContent={
            <DiffPreview filename="web_api.tf" lines={ASG_DIFF_LINES} />
          }
        />
      </div>
    </section>
  );
}

// ── Safety & Guardrails ───────────────────────────────────────────────────────

const GUARDRAILS = [
  {
    icon: ShieldCheck,
    title: "Conservative thresholds",
    description:
      "Infralyst won't recommend rightsizing unless CPU and memory P99.5 (or P95 for longer windows) are comfortably below 40% across the ASG fleet. A single spike won't trigger a false recommendation.",
  },
  {
    icon: Calendar,
    title: "Seasonality-aware",
    description:
      "For ASGs with 60+ days of data, P95 utilization is checked per weekly bucket over the full observation window. If any week exceeds 40%, the ASG is excluded. No recommendation, no risk.",
  },
  {
    icon: RefreshCw,
    title: "Zero downtime instance refresh",
    description:
      "Every PR includes an instance refresh block with a calculated min_healthy_percentage. AWS replaces instances one batch at a time so your fleet stays up during the rollout. Single-instance ASGs get a downtime warning in the PR instead.",
  },
  {
    icon: GitPullRequest,
    title: "PR-only changes",
    description:
      "Infralyst never applies changes directly. Every recommendation is delivered as a notification in Slack or in our console. You trigger PR generation, review, test, and merge on your timeline.",
  },
] as const;

// ── FAQ ───────────────────────────────────────────────────────────────────────

const ASG_FAQ_ITEMS: readonly FAQItem[] = [
  {
    question: "Can you rightsize an Auto Scaling Group without service interruption?",
    answer:
      "Yes. ASGs support rolling instance refresh, which replaces instances one batch at a time while maintaining a healthy minimum. The new instances launch with the updated instance type while old ones drain. Infralyst generates a Terraform PR with the instance refresh block preconfigured so the rollout is zero downtime by default.",
  },
  {
    question: "What's the difference between rightsizing an ASG and a standalone EC2 instance?",
    answer:
      "Standalone EC2 instances require a stop and start to change the instance type, which means downtime. ASGs can replace instances gradually using a rolling instance refresh with no service interruption. Infralyst handles both: ASG PRs include an instance refresh block, while EC2 PRs include a downtime warning so your team can plan accordingly.",
  },
  {
    question: "What thresholds does Infralyst use when analyzing Auto Scaling Groups?",
    answer:
      "For ASGs with 1 to 60 days of data, Infralyst requires CPU and memory P99.5 to stay below 40% on 5-minute CloudWatch metrics. For 60 to 365 days, it checks P95 on hourly metrics and runs a weekly seasonality check. If any single week exceeds 40%, the ASG is excluded. Every instance in the fleet must clear the bar.",
  },
  {
    question: "How much CloudWatch data does Infralyst need before recommending an ASG resize?",
    answer:
      "At least 30 days, with a minimum of 8,640 total data points across the fleet. Less data means too little signal to tell whether utilization is genuinely low or the fleet was just quiet. ASGs that don't meet either bar are excluded entirely rather than risk a false positive.",
  },
  {
    question: "Which ASGs does Infralyst skip?",
    answer:
      "Three cases: ASGs where the current instance type is already the smallest in its family, ASGs with instance store volumes attached to the launch template, and ASGs that don't have enough CloudWatch history or data point coverage to pass the data quality gate.",
  },
  {
    question: "How does instance refresh work when resizing an Auto Scaling Group?",
    answer:
      "Every ASG PR includes a Terraform instance refresh block with a rolling strategy and a calculated min_healthy_percentage. AWS replaces instances one batch at a time so your fleet stays up during the rollout. For single-instance ASGs, Infralyst adds a downtime warning to the PR instead of an instance refresh.",
  },
  {
    question: "Is it safe to downsize an Auto Scaling Group in production?",
    answer:
      "Infralyst only recommends downsizing when CPU and memory P99.5 are below 40% across every instance in the ASG fleet, including weekly seasonality checks on longer data windows. The PR uses a rolling instance refresh so instances are replaced gradually. Your team reviews and merges the PR. Nothing changes in AWS until then.",
  },
  {
    question: "How does Infralyst handle ECS-managed Auto Scaling Groups?",
    answer:
      "Infralyst detects ECS-managed ASGs by checking for the AmazonECSManaged tag. These ASGs are analyzed the same way as standard ASGs. CPU and memory thresholds, data quality gates, and seasonality checks all apply. The Terraform PR updates the launch template instance type, and ECS handles task placement on the new instances after the refresh.",
  },
  {
    question: "What happens if an ASG resize causes problems after the PR is merged?",
    answer:
      "Infralyst doesn't merge PRs. Your team does, after reviewing the utilization data in the PR body. If something goes wrong, reverting is a standard PR against the same Terraform file. The change is isolated to one instance_type argument in the launch template, so the revert is fast and straightforward.",
  },
  {
    question: "Does Infralyst analyze each instance in an ASG separately or the fleet as a whole?",
    answer:
      "Both. Infralyst pulls CloudWatch metrics per instance and evaluates each one against the P99.5 threshold. Every instance in the fleet must pass individually. The aggregate data point count across all instances must also meet the minimum threshold. One hot instance is enough to exclude the entire ASG from a recommendation.",
  },
] as const;

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Asg({
  articles,
}: {
  articles: Article[];
}): React.ReactElement {
  return (
    <Layout
      title="Auto Scaling Group Rightsizing"
      description="Infralyst finds over-provisioned ASGs and generates Terraform PRs to reduce your AWS costs, updating the launch template with zero downtime instance refresh."
    >
      <Hero />
      <TheProblem />
      <ProductHowItWorks steps={ANALYSIS_STEPS} />
      <ProductThresholds
        subtitle="Every gate and threshold Infralyst checks before recommending ASG rightsizing. These are intentionally conservative. We'd rather miss a savings opportunity than recommend a change that causes problems."
        gates={GATES}
      />
      <WhatThePrLooksLike />
      <TriggerFromSlack />
      <ProductGuardrails
        guardrails={GUARDRAILS}
        crossLink={
          <>
            Also available for{" "}
            <Link href="/products/ec2" className="text-primary hover:underline">
              EC2 Instances
            </Link>
            ,{" "}
            <Link
              href="/products/ecs-fargate"
              className="text-primary hover:underline"
            >
              ECS Fargate
            </Link>
            , and{" "}
            <Link href="/products/ebs" className="text-primary hover:underline">
              EBS Volumes
            </Link>
            .
          </>
        }
      />
      <FAQ
        items={ASG_FAQ_ITEMS}
        title="ASG Rightsizing FAQ"
        highlightWord="Rightsizing"
        className="py-16 px-4"
      />
      <LatestArticles
        articles={articles}
        title={
          <>
            Learn more about ASG{" "}
            <span className="text-primary">optimization</span>
          </>
        }
        subtitle={null}
      />
      <CallToAction
        heading="Start rightsizing your AWS Auto Scaling Groups"
        subheading="Connect your AWS account and Infralyst will find ASGs worth rightsizing. Generate a Terraform PR that updates the launch template and handles the instance refresh, when you're ready."
      />
    </Layout>
  );
}
